import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { useCart } from '../contexts/CartContext';
import { bundleAssetUrl, bundleImage } from './BundlesPage';

function BundlePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [cartError, setCartError] = useState(null);
  const [activeImage, setActiveImage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${API_BASE_URL}/bundles/${id}`);
        if (!cancelled && response.data.success) {
          setBundle(response.data.data);
          setActiveImage(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.status === 404
              ? 'This bundle could not be found.'
              : err.response?.data?.error || err.message || 'Failed to load bundle'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleAddToCart = () => {
    if (!bundle) return;
    setCartError(null);

    const presentation = bundle.presentation || {};
    const image = bundleImage(bundle);
    const result = addToCart({
      itemType: 'bundle',
      bundleId: bundle.id,
      bundleTitle: bundle.title,
      designIds: bundle.designIds || [],
      designTitle: bundle.title,
      designDescription: bundle.description || '',
      formatId: presentation.kind === 'format' ? presentation.formatId : null,
      formatName: presentation.kind === 'custom' ? presentation.label : presentation.formatId,
      presentation,
      price: Number(bundle.price),
      imageUrl: image,
      quantity,
      type: bundle.type || 'Physical'
    });

    if (result.success) {
      setAdded(true);
      setTimeout(() => setAdded(false), 2500);
    } else {
      setCartError(result.error || 'Could not add this bundle to your cart.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading bundle...</p>
        </div>
      </div>
    );
  }

  if (error || !bundle) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-red-800 mb-2">Bundle unavailable</h2>
            <p className="text-red-600 mb-4">{error || 'Bundle not found'}</p>
            <button
              onClick={() => navigate('/bundles')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Browse bundles
            </button>
          </div>
        </div>
      </div>
    );
  }

  const image = bundleImage(bundle);
  const gallery = [];
  if (bundle.coverImage) gallery.push(bundleAssetUrl(bundle.coverImage));
  (bundle.images || []).forEach((url) => {
    const resolved = bundleAssetUrl(url);
    if (resolved && !gallery.includes(resolved)) gallery.push(resolved);
  });
  const firstDesign = (bundle.designs || []).find((d) => d && !d.missing);
  if (gallery.length === 0 && firstDesign) {
    gallery.push(`${API_BASE_URL}/designs/${firstDesign.id}/thumbnail`);
  }
  const mainImage = activeImage || gallery[0] || image;
  const presentationLabel =
    bundle.presentation?.kind === 'custom' ? bundle.presentation.label : bundle.presentation?.formatId;
  const designs = (bundle.designs || []).filter((d) => d && !d.missing);

  return (
    <div className="min-h-screen py-8 lg:py-12 px-4 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => navigate('/bundles')}
          className="mb-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
        >
          ← Back to bundles
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
          <div className="bg-white rounded-xl border border-gray-200 p-3">
            <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
              {mainImage ? (
                <img src={mainImage} alt={bundle.title} className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">No image</div>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {gallery.map((url, index) => (
                  <button
                    key={`${url}-${index}`}
                    type="button"
                    onClick={() => setActiveImage(url)}
                    className={`w-16 h-16 rounded overflow-hidden border-2 transition-colors ${
                      mainImage === url ? 'border-blue-500' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    aria-label={`View image ${index + 1}`}
                  >
                    <img src={url} alt="" className="w-full h-full object-contain bg-gray-100" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{bundle.title}</h1>
            {bundle.theme && (
              <p className="mt-1 text-xs uppercase tracking-wide text-gray-500">{bundle.theme}</p>
            )}
            {bundle.description && (
              <p className="mt-3 text-gray-700 whitespace-pre-line">{bundle.description}</p>
            )}

            <p className="mt-4 text-sm text-gray-600">
              {designs.length} design{designs.length !== 1 ? 's' : ''}
              {presentationLabel ? ` · ${presentationLabel}` : ''}
            </p>

            <div className="mt-5 text-3xl font-bold text-gray-900">
              ${Number(bundle.price).toFixed(2)}
            </div>

            <div className="mt-5 flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700" htmlFor="bundle-qty">
                Qty
              </label>
              <div className="inline-flex items-center border border-gray-300 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="px-3 py-2 text-gray-700 hover:bg-gray-100"
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <input
                  id="bundle-qty"
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  className="w-14 text-center py-2 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setQuantity((q) => q + 1)}
                  className="px-3 py-2 text-gray-700 hover:bg-gray-100"
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
            </div>

            {cartError && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                {cartError}
              </div>
            )}

            <button
              type="button"
              onClick={handleAddToCart}
              className="mt-5 w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              {added ? 'Added to cart ✓' : `Add bundle to cart — $${(Number(bundle.price) * quantity).toFixed(2)}`}
            </button>

            <p className="mt-3 text-xs text-gray-500">
              Only the bundled set is sold here. Individual designs can also be purchased separately.
            </p>
          </div>
        </div>

        {designs.length > 0 && (
          <section className="mt-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Designs in this bundle</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {designs.map((design) => (
                <Link
                  key={design.id}
                  to={`/design/${design.id}`}
                  className="group bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                >
                  <div className="aspect-square bg-gray-100 overflow-hidden">
                    <img
                      src={`${API_BASE_URL}/designs/${design.id}/thumbnail`}
                      alt={design.title}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = bundleAssetUrl(design.thumbnail_url) || '';
                      }}
                    />
                  </div>
                  <div className="p-2 md:p-3">
                    <p className="text-sm font-medium text-gray-900 line-clamp-1">{design.title}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default BundlePage;
